import { createRouter, createWebHistory } from 'vue-router'
import { loadLayoutMiddleware } from '@/router/middleware/loadLayoutMiddleware.js'
import { authMiddleware } from '@/router/middleware/auth.js'
import { maintenanceModeMiddleware } from '@/router/middleware/maintenanceMode.js'
import { utmTrackingMiddleware } from '@/router/middleware/utmTracking.js'
import { redirectMiddleware } from '@/router/middleware/redirects.js'
import setPageTitle from '@/helpers/set-page-title.js'
import { hasListingScrollPosition, markListingScrollRestore } from '@/helpers/listing-scroll-position.js'

const router = createRouter({
  history: createWebHistory('/'),
  scrollBehavior(to, from, savedPosition) {
    // This hook cannot scroll anything in this app, and is not trying to. The window never
    // scrolls - #app-layout is height:100vh with overflow hidden and the only scroller is
    // <main id="page-content"> (src/assets/scss/main.scss) - while vue-router applies
    // whatever is returned here via window.scrollTo, so any position object would be a
    // silent no-op. The actual scrolling is done by the views, against #page-content.
    //
    // What this hook is still the only place able to do is tell a back/forward navigation
    // apart from a fresh one: savedPosition is populated on popstate and nothing else. The
    // listing views need that signal to know whether to restore, and cannot read it
    // themselves, so record it for them here.
    if (savedPosition && hasListingScrollPosition(to.fullPath)) {
      markListingScrollRestore(to.fullPath);
    }

    // Returning false rather than a position, so that "the router does no scrolling" is
    // explicit instead of looking like behaviour that happens to be inert. Listing routes
    // put #page-content where it belongs themselves once their products have rendered -
    // see helpers/listing-scroll-position.js.
    return false;
  },
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: () => import('../views/Dashboard.vue'),
      meta: {
        pageTitle: 'Dashboard'
      }
    },
    {
      path: '/full-catalogue',
      name: 'full-catalogue',
      component: () => import('../views/CatalogueView.vue'),
      meta: {
        title: 'FULL CATALOGUE',
        subtitle: 'Choose the category you wish to browse below.',
        categoryGetter: 'mySmiMenu/rootMenuItems',
        scope: 'full',
        isUserCatalogue: false,
        pageTitle: 'Full Catalogue'
      }
    },
    {
      path: '/my-catalogue',
      name: 'my-catalogue',
      component: () => import('../views/CatalogueView.vue'),
      meta: {
        title: 'MY CATALOGUE',
        subtitle: 'Choose the category you wish to browse below.',
        categoryGetter: 'mySmiMenu/rootMyMenuItems',
        scope: 'my',
        isUserCatalogue: false,
        pageTitle: 'My Catalogue'
      }
    },
    {
      path: '/user-catalogues',
      name: 'user-catalogues',
      component: () => import('../views/CatalogueView.vue'),
      meta: {
        title: 'USER CATALOGUES',
        subtitle: 'Choose the catalogue you wish to order for below.',
        categoryGetter: 'mySmiMenu/rootMenuItems',
        isUserCatalogue: true,
        pageTitle: 'User Catalogues'
      }
    },
    {
      path: '/product/:productSlug',
      name: 'single-product',
      component: () => import('../views/SingleProduct.vue'),
      meta: {
        pageTitle: 'Product'
      }
    },

    // All catalogues root - shows all products across all categories
    {
      path: '/categories/all',
      name: 'all-products-root',
      component: () => import('../views/ProductListV2.vue'),
      exact: true,
      meta: {
        pageTitle: 'All Products'
      }
    },

    // All catalogues (combined scope - shows all products user can see)
    {
      path: '/categories/all/:fullSlug(.*)+',
      name: 'all-product-cats',
      component: () => import('../views/ProductListV2.vue'),
      meta: {
        pageTitle: 'Catalogue'
      }
    },

    // My and Full catalogues
    {
      path: '/categories/:prefix(my|full)/:fullSlug(.*)*',
      name: 'product-cats',
      component: () => import('../views/ProductList.vue'),
      meta: {
        pageTitle: 'Catalogue'
      }
    },

    // User-specific catalogues
    {
      path: '/categories/user/:catalogueId/:fullSlug(.*)*',
      name: 'user-product-cats',
      component: () => import('../views/ProductList.vue'),
      meta: {
        pageTitle: 'Catalogue'
      }
    },

    {
      path: '/product-category',
      name: 'product-category',
      component: () => import('../views/ProductList.vue'),
      meta: {
        pageTitle: 'Catalogue'
      }
    },
    {
      path: '/basket',
      name: 'basket',
      component: () => import('../views/Basket.vue'),
      meta: {
        layout: 'BasketLayout',
        pageTitle: 'Basket'
      }
    },
    {
      path: '/punchedout-baskets',
      name: 'punchout baskets',
      component: () => import('../views/punchedoutBaskets.vue'),
      meta: {
        pageTitle: 'Punchout Baskets'
      }
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('../views/Checkout.vue'),
      meta: {
        layout: 'BasketLayout',
        pageTitle: 'Checkout'
      }
    },
    {
      path: '/order-complete',
      name: 'order-complete',
      component: () => import('../views/OrderComplete.vue'),
      meta: {
        pageTitle: 'Order Complete'
      }
    },
    {
      path: '/order-history',
      name: 'order-history',
      component: () => import('../views/OrderHistory.vue'),
      meta: {
        pageTitle: 'Order History'
      }
    },
    {
      path: '/order-to-approve',
      name: 'order-to-approve',
      component: () => import('../views/OrderApprove.vue'),
      meta: {
        pageTitle: 'Order To Approve'
      }
    },
    {
      path: '/wearer-pack',
      name: 'wearer-pack',
      component: () => import('../views/WearerPack.vue'),
      meta: {
        pageTitle: 'Wearer Pack'
      }
    },
    {
      path: '/reporting',
      name: 'reporting',
      component: () => import('../views/Reporting.vue'),
      meta: {
        pageTitle: 'Reporting'
      }
    },
    {
      path: '/add-wearer',
      name: 'add-wearer',
      component: () => import('../views/NewWearer.vue'),
      meta: {
        pageTitle: 'Add Wearer'
      }
    },
    {
      path: '/remove-wearer',
      name: 'remove-wearer',
      component: () => import('../views/RemoveWearer.vue'),
      meta: {
        pageTitle: 'Remove Wearer'
      }
    },
    {
      path: '/add-site-user',
      name: 'add-site-user',
      component: () => import('../views/NewSiteUser.vue'),
      meta: {
        pageTitle: 'Add Site User'
      }
    },
    {
      path: '/add-delivery-address',
      name: 'add-delivery-address',
      component: () => import('../views/NewDeliveryAddress.vue'),
      meta: {
        pageTitle: 'Add Delivery Address'
      }
    },
    {
      path: '/manage-delivery-addresses',
      name: 'manageAdresses',
      component: () => import('../views/manageAddresses.vue'),
      meta: {
        pageTitle: 'Manage Delivery Addresses'
      }
    },
    {
      path: '/my-account',
      name: 'my-account',
      component: () => import('../views/MyAccount.vue'),
      meta: {
        pageTitle: 'My Account'
      }
    },
    {
      path: '/news',
      name: 'news',
      component: () => import('../components/cms/article.vue'),
      meta: {
        pageTitle: 'News'
      }
    },
    {
      path: '/news/:slug',
      name: 'NewsArticle',
      component: () => import('../components/cms/pages/ContentArticle.vue'),
      meta: {
        pageTitle: 'News'
      }
    },
    {
      path: '/dashboard',
      name: 'dashboard-alt',
      component: () => import('../views/Dashboard.vue'),
      meta: {
        pageTitle: 'Dashboard'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Login'
      }
    },
    {
      path: '/punchout-login-assistance',
      name: 'punchout-login-assistance',
      component: () => import('../views/PunchoutLoginAssistance.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Punchout Login Assistance'
      }
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: () => import('../views/Maintenance.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Maintenance'
      }
    },
    {
      path: '/punchout-auth-error',
      name: 'punchout-auth-error',
      component: () => import('../views/PunchoutAuthError.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Punchout Authentication Error'
      }
    },
    {
      path: '/punchout-only',
      name: 'punchout-only',
      component: () => import('../views/PunchoutOnly.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Punchout Only'
      }
    },
    {
      name: 'select-business-unit-entity',
      path: '/select-your-customer',
      component: () => import('../views/businessUnits.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Select Your Customer'
      }
    },
    {
      path: '/customer/forgot',
      name: 'forgot',
      component: () => import('../views/Forgot.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Forgot Password'
      }
    },
    {
      path: '/customer/reset-password/:slug',
      name: 'reset-password',
      component: () => import('../views/ResetPassword.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Reset Password'
      }
    },
    {
      path: '/customer/invitation',
      name: 'invitation',
      component: () => import('../views/Invitation.vue'),
      meta: {
        layout: 'LoginLayout',
        pageTitle: 'Invitation'
      }
    },
    {
      path: '/site-search',
      name: 'site-search',
      component: () => import('../views/Search.vue'),
      meta: {
        pageTitle: 'Search'
      }
    },
    {
      path: '/search',
      name: 'search',
      component: () => import('../views/SearchResults.vue'),
      meta: {
        pageTitle: 'Search Results'
      }
    },
    {
      path: '/help',
      name: 'help',
      component: () => import('../views/Help.vue'),
      meta: {
        pageTitle: 'Help'
      }
    },
    {
      path: '/order-to-approve/:orderId',
      name: 'OrderToApprove',
      component: () => import('@/components/order-to-approve/_orderId.vue'),
      meta: {
        pageTitle: 'Order To Approve'
      }
    },

    {
      path: '/cms/:slug',
      name: 'cms',
      component: () => import('@/components/cms/CmsPage.vue'),
      meta: {
        pageTitle: 'CMS'
      }
    },
    {
      path: '/basket/checkout-success',
      name: 'checkout-success',
      component: () => import('@/components/Basket/checkout-success.vue'),
      meta: {
        pageTitle: 'Checkout Success'
      }
    },
    {
      path: '/order-history/:orderId',
      name: 'OrderHistory',
      component: () => import('@/components/orderHistory/_orderId.vue'),
      meta: {
        pageTitle: 'Order History'
      }
    },
    {
      path: '/order-history/:orderId/despatched/:id',
      name: 'OrderFulfilled',
      component: () => import('@/components/orderHistory/despatched/DespatchedDetail.vue'),
      meta: {
        pageTitle: 'Order Despatched'
      }
    },
    {
      path: '/admin/active-users',
      name: '',
      component: () => import('../views/admin/active-users.vue'),
      meta: {
        pageTitle: 'Admin - Active Users',
        permission: 'view_users'
      }
    },
    {
      path: '/admin/invitation',
      name: 'admin-invitation',
      component: () => import('../views/admin/Invitation.vue'),
      meta: {
        pageTitle: 'Admin - Invitation',
        permission: 'manage_invitations'
      }
    },
    {
      path: '/admin/my-catalogue',
      name: '',
      component: () => import('../views/admin/my-catalogue.vue'),
      meta: {
        pageTitle: 'Admin - My Catalogue',
        permission: 'manage_catalogue_requests'
      }
    },
    {
      path: '/admin/netsuite-pull',
      name: '',
      component: () => import('../views/admin/netsuite-pull.vue'),
      meta: {
        pageTitle: 'Admin - NetSuite Pull',
        permission: 'netsuite_sync'
      }
    },
    {
      path: '/admin/orders',
      name: '',
      component: () => import('../views/admin/orders.vue'),
      meta: {
        pageTitle: 'Admin - Orders',
        permission: 'manage_orders'
      }
    },
    {
      path: '/admin/promo',
      name: '',
      component: () => import('../views/admin/promo.vue'),
      meta: {
        pageTitle: 'Admin - Promotions',
        permission: 'site_settings'
      }
    },
    {
      path: '/admin/notifications',
      name: 'admin-notifications',
      component: () => import('../views/admin/notifications.vue'),
      meta: {
        pageTitle: 'Admin - Notifications',
        permission: 'notification_banner'
      }
    },
    {
      path: '/admin/sync-status',
      name: '',
      component: () => import('../views/admin/sync-status.vue'),
      meta: {
        pageTitle: 'Admin - Sync Status',
        permission: 'view_sync_status'
      }
    },
    {
      path: '/admin/view-as',
      name: '',
      component: () => import('../views/admin/view-as.vue'),
      meta: {
        pageTitle: 'Admin - View As',
        permission: 'impersonate_users'
      }
    },
    {
      path: '/admin/user/:userId',
      name: '',
      component: () => import('../views/admin/user/_id.vue'),
      meta: {
        pageTitle: 'Admin - User',
        permission: 'edit_users'
      }
    },
    {
      path: '/admin/category-manager',
      name: '',
      component: () => import('../views/admin/category-manager.vue'),
      meta: {
        pageTitle: 'Admin - Category Manager',
        permission: 'manage_categories'
      }
    },
    {
      path: '/admin/manage-admin-users',
      name: 'admin-manage-users',
      component: () => import('../views/admin/ManageAdminUsers.vue'),
      meta: {
        pageTitle: 'Admin - Manage Users',
        permission: 'manage_admin_users'
      }
    },
    {
      path: '/admin/login-history',
      name: 'admin-login-history',
      component: () => import('../views/admin/LoginHistory.vue'),
      meta: {
        pageTitle: 'Admin - Login History',
        permission: 'view_login_history'
      }
    },
    {
      path: '/admin/search-terms',
      name: 'admin-search-terms',
      component: () => import('../views/admin/SearchTerms.vue'),
      meta: {
        pageTitle: 'Admin - Search Terms',
        permission: 'view_search_terms'
      }
    },
    {
      path: '/admin/landing-pages',
      name: 'admin-landing-pages',
      component: () => import('../views/admin/LandingPages.vue'),
      meta: {
        pageTitle: 'Admin - Landing Pages',
        permission: 'manage_landing_pages'
      }
    },
    {
      path: '/admin/landing-pages/:id/products',
      name: 'admin-landing-page-products',
      component: () => import('../views/admin/LandingPageProducts.vue'),
      meta: {
        pageTitle: 'Admin - Landing Page Products',
        permission: 'manage_landing_pages'
      }
    },
    {
      path: '/admin/landing-pages/:id/customers',
      name: 'admin-landing-page-customers',
      component: () => import('../views/admin/LandingPageCustomers.vue'),
      meta: {
        pageTitle: 'Admin - Landing Page Customers',
        permission: 'manage_landing_pages'
      }
    },
    {
      path: '/admin/training-guides',
      name: 'admin-training-guides',
      component: () => import('../views/admin/TrainingGuides.vue'),
      meta: {
        pageTitle: 'Admin - Training Guides',
        permission: 'manage_training_guides'
      }
    },
    {
      path: '/admin/roles',
      name: 'admin-roles',
      component: () => import('../views/admin/RoleManagement.vue'),
      meta: {
        pageTitle: 'Admin - Roles',
        permission: 'manage_roles'
      }
    },
    {
      path: '/spotlights',
      name: 'landing-pages',
      component: () => import('../views/LandingPages.vue'),
      meta: {
        pageTitle: 'Product Spotlights'
      }
    },
    {
      path: '/spotlights/:slug',
      name: 'landing-page',
      component: () => import('../views/LandingPage.vue'),
      meta: {
        pageTitle: 'Product Spotlight'
      }
    },
    {
      path: '/campaigns/:slug',
      name: 'campaign-page',
      component: () => import('../views/LandingPage.vue'),
      meta: {
        pageTitle: 'Campaign'
      }
    },
    {
      path: '/promotions/:slug',
      name: 'promotion-page',
      component: () => import('../views/LandingPage.vue'),
      meta: {
        pageTitle: 'Promotion'
      }
    },
    {
      path: '/saved-baskets',
      name: '',
      component: () => import('../views/savedBasket.vue'),
      meta: {
        pageTitle: 'Saved Baskets'
      }
    },
    {
      path: '/saved-punchout-baskets',
      name: '',
      component: () => import('../views/savedPunchoutBasket.vue'),
      meta: {
        pageTitle: 'Saved Punchout Baskets'
      }
    },
  ]
});

router.beforeEach(redirectMiddleware);
router.beforeEach(maintenanceModeMiddleware);
router.beforeEach(loadLayoutMiddleware);
router.beforeEach(authMiddleware);
router.beforeEach(utmTrackingMiddleware);

router.afterEach((to) => {
  setPageTitle(to.meta.pageTitle);
});

export default router;
